<script>
  import { sortBy } from 'lodash';
  import NotificationDrawerItem from '@/components/base/NotificationDrawerItem.vue';
  import moment from 'moment';
  import { GameLauncher } from '@/mixins/game-launcher';
  import { BANK_IDS } from '@/store';

  export default {
    name: 'notifications',

    components: { NotificationDrawerItem },

    mixins: [GameLauncher],

    data() {
      return {
        perPage: 7,
        displayCount: 7,
        alertMessages: [],
      };
    },

    computed: {
      notifications() {
        return sortBy([...this.alertMessages], 'timestamp')
          .reverse()
          .slice(0, this.displayCount);
      },
      totalItems() {
        return [...this.alertMessages].filter((item) => !item.deleted).length;
      },
    },

    watch: {
      '$store.getters.alerts': {
        deep: true,
        immediate: true,
        handler(val) {
          if (val?.length >= this.alertMessages?.length) {
            this.alertMessages = [...val];
          }
        },
      },
    },

    mounted() {
      this.drawLinesBetweenDots();
    },

    methods: {
      async acceptChallenge(privateRoomId, item, alertKey) {
        const { balance } = this.$store.getters.getUserTotalBalance;
        let bankId = item.coin === 'QC' || item.coin === BANK_IDS.MQC ? BANK_IDS.MQC : BANK_IDS.MMC;
        const hasBalance = bankId === BANK_IDS.MQC ? balance.MQC > 0 : balance.MMC > 0;
        let isBalanceSufficient =
          bankId === BANK_IDS.MQC ? balance.MQC >= (item.buyIn || 0) : balance.MMC >= (item.buyIn || 0);

        if (!hasBalance || !isBalanceSufficient) {
          await this.$store.dispatch('showConfirmDialog', {
            title: 'Insufficient balance',
            text: 'In order to accept this invitation, please deposit to your balance.',
            yesText: 'Deposit Now',
            noText: 'Cancel',
            callback: () => {
              this.$store.dispatch('setWalletDialog', {
                value: true,
                tab: 'purchase',
              });
            },
          });
          return;
        }

        if (this.isUnityGame(item.gameId)) {
          const homeUrl = process.env.VUE_APP_CHALLENGE_URL;
          const addFriendUrl = process.env.VUE_APP_ADD_FRIEND_URL;
          await this.launchGameUnity(
            item.gameId,
            homeUrl,
            addFriendUrl,
            'extended',
            item.buyIn,
            false,
            item.hostId,
            privateRoomId,
            item.coin,
          );
          await this.$store.dispatch('clearsinglealert', alertKey);
        } else {
          await this.$store.dispatch('clearsinglealert', alertKey);

          let response = await this.$store.dispatch('acceptchallenge', {
            privateRoomId: privateRoomId,
          });

          await this.$store.dispatch('clearsinglealert', alertKey);

          if (!response.haserror) {
            const addFriendUrl = process.env.VUE_APP_ADD_FRIEND_URL;
            await this.enterPrivateRoom(privateRoomId, 'CAF', addFriendUrl);
          }
        }
      },
      async declineChallenge(privateRoomId, alertKey) {
        await this.$store.dispatch('declineChallenge', {
          privateRoomId: privateRoomId,
        });

        await this.$store.dispatch('clearsinglealert', alertKey);

        await this.$store.dispatch('clearsinglechallenge', privateRoomId);
      },
      clearSingleMessage(key) {
        this.$store.dispatch('clearsinglealert', key);
      },
      acceptFriend(friendId, key) {
        this.$store.dispatch('acceptfriendrequest', friendId);
        this.$store.dispatch('clearsinglealert', key);
      },
      rejectFriend(friendId, key) {
        this.$store.dispatch('rejectfriendrequest', friendId);
        this.$store.dispatch('clearsinglealert', key);
      },
      isUnityGame(gameId) {
        if (this.$store.getters.getActiveGames.length > 0) {
          const game = this.$store.getters.getActiveGames.find((game) => game.key === String(gameId));
          return game ? game.unityGame : false;
        } else {
          return false;
        }
      },
      drawLinesBetweenDots() {
        const timelineItems = [...document.querySelectorAll('.timeline-item')];

        timelineItems.forEach((currentDot, i) => {
          const nextDot = timelineItems[i + 1];

          if (nextDot) {
            const currentDotPosition = currentDot.getBoundingClientRect().top;
            const nextDotPosition = nextDot.getBoundingClientRect().top;
            const distance = nextDotPosition - currentDotPosition;

            currentDot.style.setProperty('--line-height', `${distance - 24}px`);
          }
        });
      },
      loadMore() {
        this.displayCount += this.perPage;

        this.$nextTick(() => {
          this.drawLinesBetweenDots();
        });
      },
      formatTime(timestamp) {
        return moment(timestamp).format('MMMM D, YYYY');
      },
    },
  };
</script>

<template>
  <v-container>
    <div class="mb-4 d-flex align-center">
      <span class="text-h6 white--text mr-4">All Notifications</span>
      <span class="pa-1 text-h5 font-weight-600 notifications-count white--text">{{ totalItems }}</span>
    </div>

    <hr class="mb-10" />

    <ul class="pl-0 pr-3 notifications w-full">
      <li v-for="(item, index) of notifications" :key="index">
        <div :class="{ deleted: item.deleted }" class="notification">
          <notification-drawer-item
            :item="item"
            :time-format="$vuetify.breakpoint.width < 768 ? 'hh:mm A - MMMM D, YYYY' : 'HH:mm'"
            dense
            @accept="acceptChallenge"
            @acceptFriend="acceptFriend"
            @clear="clearSingleMessage"
            @deny="declineChallenge"
            @rejectFriend="rejectFriend"
          />
        </div>

        <div :class="{ deleted: item.deleted }" class="timeline-item">{{ formatTime(item.timestamp) }}</div>
      </li>
      <li v-if="!notifications.length">
        <div class="notification d-flex justify-center">
          <span class="superbold">Empty</span>
        </div>
      </li>
    </ul>

    <div v-if="displayCount < alertMessages.length" class="d-flex justify-center my-7">
      <v-btn class="border-doubled gold large" @click="loadMore">Load More</v-btn>
    </div>
  </v-container>
</template>

<style lang="scss" scoped>
  .notifications-count {
    background: red;
    border-radius: 8px;
    text-align: center;
    width: 37px;
  }

  .notifications {
    li {
      display: flex;
      align-items: center;

      .timeline-item {
        margin-left: 100px;
        color: white;
        font-size: 14px;
        position: relative;
        width: 150px;

        &::before {
          content: '';
          position: absolute;
          width: 24px;
          height: 24px;
          left: -30px;
          top: 0;
          background: white;
          border-radius: 50%;
          margin-right: 10px;
        }

        &::after {
          content: '';
          position: absolute;
          width: 2px;
          height: var(--line-height);
          background-color: white;
          left: -19px;
          top: 24px;
        }

        &.deleted {
          color: lightgray;

          &::before {
            background: transparent;
            border: 2px solid #b9bbbd;
          }
        }

        @media screen and (max-width: 768px) {
          display: none;
        }
      }

      &:last-of-type {
        .timeline-item::after {
          height: 0;
        }
      }
    }

    .notification {
      width: 100%;
      margin: 15px 0;
      border-radius: 8px;
      padding: 10px 20px;
      background: white;

      .request-container {
        grid-template-columns: 10px auto 50px;
      }

      .request-container-no-btn {
        grid-template-columns: 10px auto;
      }

      &::v-deep {
        .dot {
          background: var(--v-gold-base) !important;
        }

        p {
          font-size: 18px;
        }

        span.font-weight-semi.pl-1 {
          display: block;
          margin-top: 5px;
          padding-left: 0 !important;
          font-weight: 400 !important;
          color: #b0b0b0 !important;
        }
      }

      &.deleted {
        background: #b9bbbd;

        &::v-deep {
          .dot {
            background: #909295 !important;
          }

          span.font-weight-semi.pl-1 {
            color: #909295 !important;
          }
        }
      }
    }
  }
</style>
